import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subject, takeUntil } from 'rxjs';
import { DocusignService } from '@lmc-app/core/services/docusign.service';
import { AdditionalNotification, PhoneNumber, Recipient } from '@lmc-app/summary/docusign/interface/docusign-envelope-recipients-interface';
import { RecipientListSettings } from '@lmc-app/summary/docusign/constants/docusign-constants';

@Component({
  selector: 'formly-recipient-list',
  templateUrl: './recipient-list.type.html',
  styleUrls: ['./recipient-list.type.scss'],
})
export class RecipientListType extends FieldType<FieldTypeConfig> implements OnInit, OnDestroy {
  recipients: Recipient[] = [];
  loading = true;
  error: string | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(private _docusignService: DocusignService, private _cdr: ChangeDetectorRef) {
    super();
  }

  ngOnInit(): void {
    const envelopeId = this.props?.['envelopeId'] as string;
    // if no envelopeId no action
    if (!envelopeId) {
      return;
    }
    // Get the recipient list created on envelope
    this._docusignService.getEnvelopeRecipientStatus(envelopeId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        try {
          this.recipients = [...(res?.signers ?? []), ...(res?.certifiedDeliveries ?? [])].sort(
            (a, b) => a.routingOrder - b.routingOrder
          );
          // Backfills empty phoneNumber objects DocuSign can return for a channel
          this.recipients.forEach((r) => {
            this.syncPhoneNumber(r);
            this.syncEmail(r);
            this.syncName(r);
          });
          // No `key` on this field, so `model` is the parent's actual object reference
          this.model['recipients'] = this.recipients;
          this.props?.['onLoad']?.(this.recipients);
        } catch (err) {
          console.error(err);
          this.error = 'Could not load recipients.';
        } finally {
          this.loading = false;
          // OnPush panel won't repaint on its own for this async response
          this._cdr.markForCheck();
        }
      },
      error: (err) => {
        console.error(err);
        this.error = 'Could not load recipients.';
        this.loading = false;
        this._cdr.markForCheck();
      },
    });

    // Primary/Co-Applicant panels are siblings in their own OnPush panel, so editing
    // their Email/Mobile fields doesn't mark this panel dirty on its own — resync on
    // every form change so the live getters below actually repaint.
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.recipients.forEach((r) => {
        this.syncPhoneNumber(r);
        this.syncEmail(r);
        this.syncName(r);
      });
      this.model['recipients'] = [...this.recipients];
      this._cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getInitials(roleName?: string): string {
    return (roleName || '').slice(0, 2).toUpperCase();
  }

  // Deterministic color per initials so the same role always gets the same avatar color
  getAvatarColor(roleName?: string): string {
    const initials = this.getInitials(roleName);
    return RecipientListSettings.AVATAR_COLORS[initials] || RecipientListSettings.DEFAULT_AVATAR_COLOR;
  }

  // Reads live from the form model so it reflects the current Primary/Co-Applicant Email field
  getDisplayEmail(r: Recipient): string {
    const source = RecipientListSettings.CONTACT_SOURCE_FIELDS[this.getInitials(r.roleName)];
    const liveEmail = source ? this.model?.[source.email]?.value : null;
    return liveEmail || r.email;
  }

  // Same live-read approach as getDisplayEmail, combining First/Middle/Last name
  getDisplayName(r: Recipient): string {
    const source = RecipientListSettings.NAME_SOURCE_FIELDS[this.getInitials(r.roleName)];
    if (!source) return r.name;
    const first = this.model?.[source.first]?.value;
    const middle = this.model?.[source.middle]?.value;
    const last = this.model?.[source.last]?.value;
    const liveName = [first, middle, last].filter((part) => !!part).join(' ');
    return liveName || r.name;
  }

  // Same live-read approach as getDisplayEmail, but for the Mobile field.
  getDisplayPhone(r: Recipient): PhoneNumber | undefined {
    const source = RecipientListSettings.CONTACT_SOURCE_FIELDS[this.getInitials(r.roleName)];
    const liveMobile = source ? this.model?.[source.mobile]?.value : null;
    if (liveMobile) {
      return { countryCode: r.phoneNumber?.countryCode ?? '', number: liveMobile };
    }

    // On load, prefer a secondary channel's number if it differs from the primary's
    const secondaryWithDifferentNumber = this.getSecondaryChannels(r).find(
      (n) => n.phoneNumber?.number && n.phoneNumber.number !== r.phoneNumber?.number
    );
    if (secondaryWithDifferentNumber) return secondaryWithDifferentNumber.phoneNumber;

    return r.phoneNumber?.number ? r.phoneNumber : undefined;
  }

  // Resolves Email/SMS/WhatsApp on/off from deliveryMethod + additionalNotifications.
  // Normalizes legacy "WhatsApp only" drafts into Email+WhatsApp.
  private getChannelState(r: Recipient): { email: boolean; sms: boolean; whatsapp: boolean } {
    const primary = (r.deliveryMethod || 'email').toLowerCase();
    const secondary = this.getSecondaryChannels(r).map((n) => n.secondaryDeliveryMethod);
    return {
      email: primary !== 'sms',
      sms: primary === 'sms' || secondary.includes('SMS'),
      whatsapp: primary === 'whatsapp' || secondary.includes('WhatsApp'),
    };
  }

  // Writes an {email, sms, whatsapp} state back onto the recipient's deliveryMethod/additionalNotifications shape.
  private applyChannelState(r: Recipient, state: { email: boolean; sms: boolean; whatsapp: boolean }): void {
    r.deliveryMethod = state.email ? 'email' : 'sms';
    r.additionalNotifications = [
      ...(state.email && state.sms ? [{ secondaryDeliveryMethod: 'SMS' as const }] : []),
      ...(state.whatsapp ? [{ secondaryDeliveryMethod: 'WhatsApp' as const }] : []),
    ];
    this.syncPhoneNumber(r);
    this.syncEmail(r);
  }

  // Fills in a real phoneNumber wherever a channel needs one, since DocuSign can
  // hand back an entry with no/empty phoneNumber, which the update payload can't use.
  private syncPhoneNumber(r: Recipient): void {
    const phoneNumber = this.getDisplayPhone(r);
    if (!phoneNumber?.number) return;
    if (r.deliveryMethod === 'sms') r.phoneNumber = phoneNumber;
    this.getSecondaryChannels(r).forEach((n) => (n.phoneNumber = phoneNumber));
  }

  // Same idea as syncPhoneNumber but for email, so the payload sends the live address
  private syncEmail(r: Recipient): void {
    const email = this.getDisplayEmail(r);
    if (email) r.email = email;
  }

  // Same idea as syncEmail but for the recipient's name
  private syncName(r: Recipient): void {
    const name = this.getDisplayName(r);
    if (name) r.name = name;
  }

  formatPhone(p?: PhoneNumber): string {
    if (!p?.number) return '';
    return p.countryCode ? `+${p.countryCode} ${p.number}` : p.number;
  }

  // Returns the extra channels (SMS / WhatsApp) layered on top of the primary channel, if any.
  // DocuSign sometimes returns a single object instead of a one-item array when
  // there's only one additionalNotifications entry — normalize both shapes here.
  getSecondaryChannels(r: Recipient): AdditionalNotification[] {
    const raw = r.additionalNotifications as unknown;
    if (!raw) return [];
    return Array.isArray(raw) ? raw : [raw as AdditionalNotification];
  }

  // If the channel was selected when draft was created select the channel
  hasChannel(r: Recipient, method: 'Email' | 'SMS' | 'WhatsApp'): boolean {
    const state = this.getChannelState(r);
    if (method === 'Email') return state.email;
    if (method === 'SMS') return state.sms;
    return state.whatsapp;
  }

  // Manager/Agent rows are informational only — their delivery channel is fixed
  isChannelReadonly(r: Recipient): boolean {
    return RecipientListSettings.READONLY_CHANNEL_ROLES.includes(this.getInitials(r.roleName));
  }

  // Toggle Email/SMS/WhatsApp. Allowed combinations: Email, SMS, Email+SMS, Email+WhatsApp.
  // Not allowed: WhatsApp alone, SMS+WhatsApp, or all three together.
  toggleChannel(r: Recipient, method: 'Email' | 'SMS' | 'WhatsApp', event: Event): void {
    if (this.isChannelReadonly(r)) return;

    const checkbox = event.target as HTMLInputElement;
    const key = method.toLowerCase() as 'email' | 'sms' | 'whatsapp';
    const isChecked = checkbox.checked;
    const state = this.getChannelState(r);

    // Apply the user's intended change
    state[key] = isChecked;

    // RULE 1: Can't be empty (default to Email)
    const allFalse = !state.email && !state.sms && !state.whatsapp;
    if (allFalse) {
      state.email = true;
    } else {
      // RULE 2: SMS and WhatsApp can't be active together
      if (key === 'whatsapp' && isChecked) state.sms = false;
      if (key === 'sms' && isChecked) state.whatsapp = false;

      // RULE 3: WhatsApp requires Email
      if (state.whatsapp) state.email = true;
      if (key === 'email' && !isChecked) state.whatsapp = false;
    }

    // Force the DOM checkbox back in sync in case a rule above reverted it
    checkbox.checked = state[key];

    this.applyChannelState(r, state);
    this.syncRecipients();
  }

  trackByEmail(_index: number, r: Recipient): string {
    return r.email;
  }

  drop(event: CdkDragDrop<Recipient[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.recipients, event.previousIndex, event.currentIndex);
    this.syncRecipients();
  }

  // Pushes recipients into the shared model and notifies the parent. Renumbers
  // recipientId/routingOrder from array position so signing order matches the screen.
  private syncRecipients(): void {
    this.recipients.forEach((r, i) => {
      r.recipientId = String(i + 1);
      r.routingOrder = i + 1;
    });
    this.model['recipients'] = [...this.recipients];
    this.props?.['onReorder']?.(this.recipients);
  }
}
